/**
 * «Tu equipo de IA» (spec §4.4): las tarjetas de los agentes disponibles,
 * por departamento. Un clic abre una conversación dirigida al agente.
 * GEN-AG-006: foto en vigor de la ficha; sin foto, la inicial.
 * GEN-AG-010: tarjeta con foto, nombre y cargo corto (sin cargo, nada).
 * GEN-PX-010: cabecera con contador y subtítulo; bajo el cargo, «Lidera» y la
 * actividad de 7 días («en n conversaciones» / «disponible»); la FOTO completa
 * de la ficha (no la miniatura); con más de 10 agentes, los 10 más activos y el
 * enlace «Ver todo el equipo». Sin halo de «trabajando ahora»: no hay señal fiable.
 */

import { useMemo } from 'react'

import { agentColorOf } from '../../lib/agent-colors.js'
import { useFotoGrandeAgente } from '../../context/FotosAgentes.jsx'
import TeamBoard from '../../components/TeamBoard.jsx'
import { actividadPorAgente, masActivos, textoActividad } from '../../lib/actividadEquipo.js'

const initials = (name) => (name ?? '').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()

function TeamCard({ agent, ringColor, conversaciones, onSelect }) {
  const fotoDe = useFotoGrandeAgente()
  const puedeIniciarChat = agent.puede_iniciar_chat !== false
  const photo = fotoDe(agent)
  return (
    <button
      type="button"
      className={'team-card team-card--dk' + (puedeIniciarChat ? '' : ' team-card--disabled')}
      onClick={puedeIniciarChat ? () => onSelect(agent) : undefined}
      disabled={!puedeIniciarChat}
      title={puedeIniciarChat ? agent.descripcion : `${agent.nombre} — próximamente`}
    >
      <span className="team-card__photo">
        <span className="team-card__photo-ini" style={{ background: ringColor || 'var(--brand-solid)' }}>{initials(agent.nombre)}</span>
        {photo && (
          <img
            src={photo}
            alt=""
            decoding="async"
            onError={e => { e.currentTarget.style.display = 'none' }}
          />
        )}
      </span>
      <span className="team-card__body">
        <span className="team-card__name">{agent.nombre}</span>
        {agent.rol && <span className="team-card__role">{agent.rol}</span>}
        {agent.lidera === true && <span className="team-card__lidera">Lidera</span>}
        {puedeIniciarChat && (
          <span className={'team-card__act' + (conversaciones > 0 ? '' : ' team-card__act--libre')}>
            {textoActividad(conversaciones)}
          </span>
        )}
        {!puedeIniciarChat && <span className="team-card__soon">Próximamente</span>}
      </span>
    </button>
  )
}

export const equipoTieneDatos = ({ disponibles }) => disponibles.length > 0

export default function EquipoIA({ disponibles, chats, palette, theme, onSelectAgent, onVerEquipo }) {
  const actividad = useMemo(() => actividadPorAgente(disponibles, chats), [disponibles, chats])
  const visibles = useMemo(() => masActivos(disponibles, actividad), [disponibles, actividad])
  const n = disponibles.length
  return (
    <TeamBoard
      agents={visibles}
      ariaLabel="Tu equipo de IA"
      overline={
        <div className="dk-equipo__cab">
          <h2 className="dk-equipo__titulo">Tu equipo de IA</h2>
          <span className="dk-equipo__n">{n} {n === 1 ? 'agente' : 'agentes'}</span>
          <span className="dk-equipo__sub">pulsa uno para hablar con él directamente</span>
        </div>
      }
      pie={visibles.length < n && onVerEquipo ? (
        <button type="button" className="dk-equipo__ver" onClick={onVerEquipo}>Ver todo el equipo</button>
      ) : null}
      renderCard={a => (
        <TeamCard
          key={a.id ?? a.nombre}
          agent={a}
          ringColor={agentColorOf(palette ?? {}, a.id, theme)}
          conversaciones={actividad.get(a.id) ?? 0}
          onSelect={onSelectAgent}
        />
      )}
    />
  )
}
